import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    IconButton,
    LinearProgress,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Delete as DeleteIcon,
    CloudUpload as CloudUploadIcon,
    PlayArrow as PlayArrowIcon,
    CheckCircle as CheckCircleIcon,
    Videocam as VideocamIcon
} from '@mui/icons-material';

/**
 * SingleVideoUpload Component
 * 
 * Handles upload of a single product video with validation and progress tracking
 * 
 * Props:
 * - value: {videoUrl, sid, duration, file_size_bytes, ...} or File object
 * - onChange: function(video) - Called when video changes
 * - onUpload: function(videoFile) - Called to handle upload
 * - onDelete: function(videoSid) - Called to handle deletion
 * - productSid: string - Product SID for upload
 * - merchantSid: string - Merchant SID for authentication
 * - mainImageUrl: string - URL of product's main image (used as thumbnail)
 * - required: boolean - Whether video is required
 * - disabled: boolean - Whether upload is disabled
 */
const SingleVideoUpload = ({
    value = null,
    onChange,
    onUpload,
    onDelete,
    productSid,
    merchantSid,
    mainImageUrl,
    required = false,
    disabled = false
}) => {
    const fileInputRef = useRef(null);
    const [video, setVideo] = useState(value);
    const [uploadStatus, setUploadStatus] = useState(null); // 'uploading', 'uploaded', 'error'
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isDeletingStatus, setIsDeletingStatus] = useState(null); // 'deleting', null
    const videoPlayerRef = useRef(null);

    // Constants
    const MAX_SIZE_BYTES = 30 * 1024 * 1024; // 30MB
    const ALLOWED_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
    const ALLOWED_EXTENSIONS = ['mp4', 'mov', 'webm'];

    // Update local state when value prop changes
    useEffect(() => {
        setVideo(value);
    }, [value]);

    // Format file size
    const formatFileSize = (bytes) => {
        if (!bytes) return 'N/A';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    // Format duration
    const formatDuration = (seconds) => {
        if (!seconds) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Validate video file
    const validateVideo = (file) => {
        // Check file type
        if (!ALLOWED_TYPES.includes(file.type)) {
            const ext = file.name.split('.').pop().toLowerCase();
            if (!ALLOWED_EXTENSIONS.includes(ext)) {
                return `Invalid video format. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`;
            }
        }

        // Check file size
        if (file.size > MAX_SIZE_BYTES) {
            return `Video is too large (${formatFileSize(file.size)}). Maximum: 30MB`;
        }

        return null;
    };

    // Handle file selection
    const handleFileSelect = useCallback((selectedFile) => {
        if (!selectedFile) return;

        setError(null);

        // Validate
        const validationError = validateVideo(selectedFile);
        if (validationError) {
            setError(validationError);
            return;
        }

        // Set video as File object
        setVideo(selectedFile);
        setUploadStatus(null);
        
        // Notify parent
        if (onChange) {
            onChange(selectedFile);
        }
    }, [onChange]);

    // Handle file input change
    const handleFileInputChange = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    // Handle drag and drop
    const handleDrop = useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
        
        if (disabled) return;

        const file = event.dataTransfer.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    }, [disabled, handleFileSelect]);

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    // Handle upload button click
    const handleUploadClick = async () => {
        if (!video || !(video instanceof File) || !onUpload) return;

        setError(null);
        setUploadStatus('uploading');
        setUploadProgress(0);

        try {
            // Simulate progress (since we don't have real progress from API)
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => Math.min(prev + 10, 90));
            }, 200);

            // Call upload handler
            const uploadedVideo = await onUpload(video);

            clearInterval(progressInterval);
            setUploadProgress(100);
            setUploadStatus('uploaded');

            // Update local state with uploaded video data
            setVideo(uploadedVideo);

            // Notify parent
            if (onChange) {
                onChange(uploadedVideo);
            }

        } catch (err) {
            setUploadStatus('error');
            setError(err.message || 'Upload failed');
        }
    };

    // Handle delete
    const handleDelete = async () => {
        if (!video) return;

        // If it's a File object (not uploaded yet), just remove it
        if (video instanceof File) {
            setVideo(null);
            setUploadStatus(null);
            setError(null);
            if (onChange) {
                onChange(null);
            }
            return;
        }

        // If it's an uploaded video, call delete handler
        if (video.sid && onDelete) {
            setIsDeletingStatus('deleting');
            setError(null);

            try {
                await onDelete(video.sid);
                setVideo(null);
                setUploadStatus(null);
                setIsDeletingStatus(null);
                
                // Notify parent
                if (onChange) {
                    onChange(null);
                }
            } catch (err) {
                setError(err.message || 'Delete failed');
                setIsDeletingStatus(null);
            }
        }
    };

    // Handle play button click
    const handlePlayClick = () => {
        setIsPlaying(true);
        if (videoPlayerRef.current) {
            videoPlayerRef.current.play();
        }
    };

    // Get video URL for display
    const getVideoUrl = () => {
        if (!video) return null;
        
        if (video instanceof File) {
            return URL.createObjectURL(video);
        }
        
        return video.videoUrl || video.video_url;
    };

    // Get file size for display
    const getFileSize = () => {
        if (!video) return null;
        
        if (video instanceof File) {
            return video.size;
        }
        
        return video.file_size_bytes;
    };

    // Get duration for display
    const getDuration = () => {
        if (!video) return null;
        
        if (video instanceof File) {
            return null; // Duration not available until uploaded
        }
        
        return video.duration;
    };

    // Check if we have a valid video (not empty array, not null, not undefined)
    const hasValidVideo = video && (video instanceof File || (video.sid && video.videoUrl));
    
    // Check if video is uploaded
    const isUploaded = video && !(video instanceof File) && video.sid;

    // Check if upload button should be shown
    const showUploadButton = video && video instanceof File && !isUploaded;

    return (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {!hasValidVideo ? (
                // Empty state - Show upload area
                <Paper
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => {
                        if (!disabled) {
                            fileInputRef.current?.click();
                        }
                    }}
                    sx={{
                        border: '2px dashed',
                        borderColor: 'grey.400',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        bgcolor: disabled ? 'grey.100' : 'background.paper',
                        '&:hover': disabled ? {} : {
                            borderColor: 'primary.main',
                            bgcolor: 'action.hover'
                        }
                    }}
                >
                    <VideocamIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        Click or drag to add video {required && '*'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Supported: MP4, MOV, WebM
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Max size: 30MB
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                        Recommended: 720p, 5-30 seconds
                    </Typography>
                </Paper>
            ) : (
                // Video selected/uploaded - Show preview
                <Box>
                    <Paper
                        sx={{
                            p: 2,
                            position: 'relative',
                            border: '1px solid',
                            borderColor: isUploaded ? 'success.main' : 'grey.300',
                            borderRadius: 2
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            {/* Video Thumbnail/Player */}
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: 200,
                                    height: 150,
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    bgcolor: 'black',
                                    flexShrink: 0
                                }}
                            >
                                {!isPlaying ? (
                                    // Show thumbnail with play button
                                    <>
                                        {/* Use video's own thumbnail if available, otherwise use mainImageUrl */}
                                        {(video?.video_thumbnail || mainImageUrl) ? (
                                            <img
                                                src={video?.video_thumbnail || mainImageUrl}
                                                alt="Video thumbnail"
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: 'grey.800'
                                                }}
                                            >
                                                <VideocamIcon sx={{ fontSize: 64, color: 'grey.600' }} />
                                            </Box>
                                        )}
                                        
                                        {/* Play Button Overlay */}
                                        {isUploaded && (
                                            <IconButton
                                                onClick={handlePlayClick}
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    bgcolor: 'rgba(0, 0, 0, 0.6)',
                                                    color: 'white',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(0, 0, 0, 0.8)'
                                                    }
                                                }}
                                            >
                                                <PlayArrowIcon sx={{ fontSize: 48 }} />
                                            </IconButton>
                                        )}

                                        {/* Deleting Overlay */}
                                        {isDeletingStatus === 'deleting' && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: 'rgba(0, 0, 0, 0.7)',
                                                    color: 'white'
                                                }}
                                            >
                                                <CircularProgress size={40} sx={{ color: 'white', mb: 1 }} />
                                                <Typography variant="body2">Deleting...</Typography>
                                            </Box>
                                        )}
                                    </>
                                ) : (
                                    // Show video player
                                    <video
                                        ref={videoPlayerRef}
                                        src={getVideoUrl()}
                                        controls
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain'
                                        }}
                                        onEnded={() => setIsPlaying(false)}
                                    />
                                )}
                            </Box>

                            {/* Video Info */}
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        {video.original_filename || video.originalFilename || (video instanceof File ? video.name : 'Product Video')}
                                    </Typography>
                                    {isUploaded && (
                                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                                    )}
                                </Box>

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    📦 Size: {formatFileSize(getFileSize())}
                                </Typography>

                                {getDuration() && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                        ⏱️ Duration: {formatDuration(getDuration())}
                                    </Typography>
                                )}

                                {isUploaded ? (
                                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, mt: 1 }}>
                                        ✅ Uploaded
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 600, mt: 1 }}>
                                        ⚠️ Not uploaded yet
                                    </Typography>
                                )}

                                {/* Delete Button */}
                                {isDeletingStatus !== 'deleting' && (
                                    <IconButton
                                        onClick={handleDelete}
                                        disabled={disabled || isDeletingStatus === 'deleting'}
                                        sx={{ mt: 1 }}
                                        color="error"
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>

                        {/* Upload Progress */}
                        {uploadStatus === 'uploading' && (
                            <Box sx={{ mt: 2 }}>
                                <LinearProgress variant="determinate" value={uploadProgress} />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Uploading... {uploadProgress}%
                                </Typography>
                            </Box>
                        )}
                    </Paper>

                    {/* Upload Button */}
                    {showUploadButton && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<CloudUploadIcon />}
                            onClick={handleUploadClick}
                            disabled={disabled || uploadStatus === 'uploading'}
                            sx={{ mt: 2 }}
                            fullWidth
                        >
                            {uploadStatus === 'uploading' ? 'Uploading...' : 'Upload Video'}
                        </Button>
                    )}
                </Box>
            )}

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".mp4,.mov,.webm,video/mp4,video/quicktime,video/webm"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
            />
        </Box>
    );
};

export default SingleVideoUpload;

